export interface CompetitorSettingsRow {
  industry: string;
  mainCompetitor: string | null;
  industryCompetitors: string | null;
}

export function resolveStartingIndustry(args: {
  defaultIndustry: string | null | undefined;
  industryIndexes: string[];
}): string | null {
  const { defaultIndustry, industryIndexes } = args;
  if (industryIndexes.length === 0) return null;
  if (defaultIndustry && industryIndexes.includes(defaultIndustry)) {
    return defaultIndustry;
  }
  return industryIndexes[0] ?? null;
}

export function findCompetitorSettingsForIndustry(
  rows: CompetitorSettingsRow[],
  industry: string | null,
): CompetitorSettingsRow | undefined {
  if (!industry) return undefined;
  return rows.find((row) => row.industry === industry);
}

export function parseIndustryCompetitors(csv: string | null | undefined, validNames: string[]): string[] {
  if (!csv) return [];
  const allowed = new Set(validNames);
  return csv
    .split(',')
    .map((value) => value.trim())
    .filter((value) => value && allowed.has(value));
}

export function seedMainCompetitorFromSettings(
  saved: CompetitorSettingsRow | undefined,
  validNames: string[],
): string | null {
  if (!saved?.mainCompetitor) return null;
  return validNames.includes(saved.mainCompetitor) ? saved.mainCompetitor : null;
}

export function seedBenchmarkFromSettings(
  saved: CompetitorSettingsRow | undefined,
  validNames: string[],
  fallbackAllNames: string[],
): string[] {
  const parsed = parseIndustryCompetitors(saved?.industryCompetitors, validNames);
  if (parsed.length > 0) return parsed;
  return fallbackAllNames;
}

type VolumeRankedBrand = { name: string; assetCount90d?: number | null };

/** Highest 90-day creative count first; missing counts last; name as the tie-break. */
export function sortBrandsByVolumeDesc<T extends VolumeRankedBrand>(brands: readonly T[]): T[] {
  return [...brands].sort((a, b) => {
    const aCount = a.assetCount90d ?? -1;
    const bCount = b.assetCount90d ?? -1;
    if (bCount !== aCount) return bCount - aCount;
    return a.name.localeCompare(b.name);
  });
}

export function pickHighestVolumeBrandName(brands: VolumeRankedBrand[]): string | null {
  return sortBrandsByVolumeDesc(brands)[0]?.name ?? null;
}

/**
 * The workspace brand pre-selection: a still-valid main competitor stays, otherwise the
 * saved one, otherwise the highest-volume brand; an empty benchmark is seeded from the
 * saved set, otherwise every brand in the industry.
 */
export function resolveCompetitorBrandSelections(args: {
  brands: VolumeRankedBrand[];
  mainCompetitor: string | null;
  benchmark: string[];
  saved: CompetitorSettingsRow | undefined;
  hasUrlMainCompetitor: boolean;
  hasUrlBenchmark: boolean;
}): { mainCompetitor: string | null; benchmark: string[] } {
  const { brands, saved } = args;
  if (brands.length === 0) {
    return { mainCompetitor: null, benchmark: [] };
  }

  const validNames = brands.map((brand) => brand.name);
  const fallbackMain =
    seedMainCompetitorFromSettings(saved, validNames) ?? pickHighestVolumeBrandName(brands);

  let mainCompetitor = args.mainCompetitor;
  if (mainCompetitor && !validNames.includes(mainCompetitor)) {
    mainCompetitor = fallbackMain;
  } else if (!mainCompetitor && !args.hasUrlMainCompetitor) {
    mainCompetitor = fallbackMain;
  }

  const prunedBenchmark = args.benchmark.filter((name) => validNames.includes(name));
  const lostAllFromPrune = args.benchmark.length > 0 && prunedBenchmark.length === 0;
  const mainIsValid = args.mainCompetitor !== null && validNames.includes(args.mainCompetitor);
  const shouldSeedBenchmark =
    prunedBenchmark.length === 0 &&
    !args.hasUrlBenchmark &&
    (lostAllFromPrune || !mainIsValid);
  const benchmark = shouldSeedBenchmark
    ? seedBenchmarkFromSettings(saved, validNames, validNames)
    : prunedBenchmark;

  return { mainCompetitor, benchmark };
}
